import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Scale, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CountrySelect } from "@/components/Country";

const CompareForm = ({ meta, country, setCountry }) => {
  const nav = useNavigate();
  const [mode, setMode] = useState("towns");
  const [towns, setTowns] = useState(["", ""]);
  const [region, setRegion] = useState("");
  const [crit, setCrit] = useState(meta.criteria.map((c) => c.key).filter((k) => k !== "families"));
  const [busy, setBusy] = useState(false);
  useEffect(() => { setRegion(""); }, [country]);
  const filled = towns.filter((t) => t.trim()).length;
  const ok = crit.length > 0 && (mode === "region" ? !!region : filled >= 2);

  const go = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/compare", { country, towns: mode === "towns" ? towns : [], region: mode === "region" ? region : "", criteria: crit });
      nav(`/compare/${data.id}`);
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };

  return (
    <div className="bg-[#F4EFE6] border border-[#E2D9CC] p-8 space-y-8" data-testid="compare-form">
      <div className="flex flex-wrap gap-4 items-center">
        <CountrySelect value={country} onChange={setCountry} testId="compare-country" className="min-w-[260px]" />
        <div className="flex gap-2">
        {[["towns", "Up to 5 towns"], ["region", "A whole region"]].map(([v, l]) => (
          <button key={v} data-testid={`compare-mode-${v}`} onClick={() => setMode(v)} className={`px-4 py-2 text-sm border transition-colors ${mode === v ? "bg-[#3B1B48] text-[#FAF7F2] border-[#3B1B48]" : "border-[#E2D9CC] hover:bg-[#EFE6F3]"}`}>{l}</button>
        ))}
        </div>
      </div>
      {mode === "towns" ? (
        <div className="space-y-3 max-w-xl">
          <datalist id="compare-towns">{meta.towns.map((t) => <option key={t} value={t} />)}</datalist>
          {towns.map((t, i) => (
            <div key={i} className="flex gap-2">
              <span className="font-display text-[#C59B27] w-6 pt-2">{i + 1}</span>
              <input list="compare-towns" data-testid={`compare-town-${i}`} value={t} onChange={(e) => setTowns(towns.map((x, j) => (j === i ? e.target.value : x)))} placeholder={`Town in ${country}`} className="flex-1 h-11 border border-[#E2D9CC] bg-white px-3 text-sm outline-none focus:border-[#3B1B48]" />
              {towns.length > 2 && <button data-testid={`compare-remove-${i}`} onClick={() => setTowns(towns.filter((_, j) => j !== i))} className="text-[#8C827A] hover:text-[#3B1B48] px-2"><X className="w-4 h-4" /></button>}
            </div>
          ))}
          {towns.length < 5 && <button data-testid="compare-add-town" onClick={() => setTowns([...towns, ""])} className="text-sm text-[#3B1B48] underline decoration-[#C59B27] ml-8">+ Add another town</button>}
        </div>
      ) : (
        <select data-testid="compare-region" value={region} onChange={(e) => setRegion(e.target.value)} className="h-11 border border-[#E2D9CC] bg-white px-3 text-sm min-w-[260px]">
          <option value="">Choose a region…</option>
          {meta.regions.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
      )}
      <div>
        <p className="eyebrow mb-3">Compare on</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {meta.criteria.map((c) => (
            <label key={c.key} data-testid={`compare-crit-${c.key}`} className={`flex gap-3 p-4 border cursor-pointer transition-colors ${crit.includes(c.key) ? "border-[#3B1B48] bg-[#FAF7F2]" : "border-[#E2D9CC] hover:bg-[#FAF7F2]"}`}>
              <input type="checkbox" checked={crit.includes(c.key)} onChange={(e) => setCrit(e.target.checked ? [...crit, c.key] : crit.filter((k) => k !== c.key))} className="mt-1 accent-[#3B1B48] w-4 h-4" />
              <span><span className="block font-display text-lg text-[#3B1B48]">{c.label}</span><span className="text-xs text-[#8C827A]">{c.hint}</span></span>
            </label>
          ))}
        </div>
      </div>
      <Button data-testid="compare-start" onClick={go} disabled={!ok || busy} className="rounded-none h-12 px-8">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Scale className="w-4 h-4 mr-2" />Compare</>}</Button>
      <p className="text-xs text-[#8C827A]">Towns with a finished guide use its ratings and facts; others are researched from Wikipedia first. Takes 2–5 minutes and creates a PDF you can sell or share.</p>
    </div>
  );
};

const DefaultPrice = () => {
  const [price, setPrice] = useState("");
  useEffect(() => { api.get("/compare/store/settings").then((r) => setPrice(String(r.data.default_price_aud))).catch(() => {}); }, []);
  const save = () => api.put("/compare/store/settings", { price_aud: Number(price) }).then(() => toast.success("Default price saved")).catch((e) => toast.error(errMsg(e)));
  return (
    <div className="flex items-end gap-2" data-testid="compare-default-price">
      <div>
        <label className="text-xs text-[#8C827A] block mb-1">Default shop price for comparisons (A$)</label>
        <input data-testid="compare-default-price-input" type="number" min="1" step="0.5" value={price} onChange={(e) => setPrice(e.target.value)} className="h-11 w-32 border border-[#E2D9CC] bg-white px-3 text-sm outline-none focus:border-[#3B1B48]" />
      </div>
      <Button data-testid="compare-default-price-save" variant="outline" onClick={save} className="rounded-none h-11">Save</Button>
    </div>
  );
};

export default function ComparePage() {
  const [meta, setMeta] = useState(null);
  const [country, setCountry] = useState("Italy");
  const [list, setList] = useState([]);
  const load = useCallback(() => api.get("/compare").then((r) => setList(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get(`/compare/criteria?country=${encodeURIComponent(country)}`).then((r) => setMeta(r.data)).catch((e) => toast.error(errMsg(e))); }, [country]);
  return (
    <div className="rise space-y-12">
      <div>
        <p className="eyebrow mb-3">Small Towns Series</p>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#3B1B48] leading-none">Compare towns</h1>
        <p className="mt-4 text-[#8C827A] max-w-2xl">Put up to five towns in one country, or a whole region, side by side on the points that matter to Australians moving there.</p>
      </div>
      {meta ? <CompareForm meta={meta} country={country} setCountry={setCountry} /> : <p className="eyebrow">Loading…</p>}
      {list.length > 0 && (
        <div data-testid="compare-list">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-4"><p className="eyebrow">Past comparisons</p><DefaultPrice /></div>
          {list.map((c) => (
            <Link key={c.id} to={`/compare/${c.id}`} data-testid={`compare-item-${c.id}`} className="flex items-center gap-4 py-4 border-b border-[#E2D9CC] hover:bg-[#EFE6F3]/60 transition-colors px-2">
              <span className="flex-1 font-display text-lg text-[#3B1B48]">{c.title}<span className="block font-sans text-xs text-[#8C827A]">{c.country || "Italy"} · {c.towns.join(" · ") || c.region}</span></span>
              <span className={`text-xs font-mono-accent uppercase ${c.status === "ready" ? "text-[#2D6A4F]" : c.status === "failed" ? "text-red-700" : "text-[#C59B27]"}`}>{c.status === "running" ? `${c.progress}%` : c.status}</span>
              {c.published && <span data-testid={`compare-in-shop-${c.id}`} className="text-xs font-mono-accent uppercase text-[#C59B27] ml-3">In shop</span>}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
